import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { FileSpreadsheet, Loader2, UploadCloud, X } from "lucide-react";
import { EntityConfig, fileToBase64, formatFileSize, hasAcceptedExtension } from "./types";
import { IresponseData, ImportResult } from "@/lib/api";
import { ImportResultModal } from "./ImportResultModal";

interface EntityImportPanelProps {
  entity: EntityConfig;
  onImport: (params: { file: string; updated: boolean }) => Promise<IresponseData<ImportResult>>;
}

export function EntityImportPanel({ entity, onImport }: EntityImportPanelProps) {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [allowUpdate, setAllowUpdate] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [resultOpen, setResultOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const importMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("Selecciona un archivo primero.");
      const base64 = await fileToBase64(file);
      return onImport({ file: base64, updated: allowUpdate });
    },
    onSuccess: (response) => {
      setResult(response.data);
      setResultOpen(true);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const acceptFile = (candidate: File | undefined | null) => {
    if (!candidate) return;
    if (!hasAcceptedExtension(candidate.name)) {
      toast.error("Formato no soportado. Sube un archivo Excel (.xlsx o .xls).");
      return;
    }
    setFile(candidate);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    acceptFile(e.dataTransfer.files?.[0]);
  };

  return (
    <div className="space-y-4">
      {/* Drag and drop zone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
        }}
        className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-6 py-10 text-center cursor-pointer transition-colors ${
          isDragging
            ? "border-primary bg-primary/5"
            : "border-border hover:border-primary/50 hover:bg-muted/30"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls"
          className="hidden"
          onChange={(e) => acceptFile(e.target.files?.[0])}
        />
        <UploadCloud className="w-8 h-8 text-muted-foreground/60" />
        <p className="text-sm text-foreground font-medium">
          Arrastra tu archivo Excel aquí o haz clic para seleccionarlo
        </p>
        <p className="text-xs text-muted-foreground">Formatos soportados: .xlsx, .xls</p>
      </div>

      {/* Selected file */}
      {file && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-2.5">
          <div className="flex items-center gap-2.5 min-w-0">
            <FileSpreadsheet className="w-4 h-4 text-emerald-500 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{file.name}</p>
              <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={() => {
              setFile(null);
              if (inputRef.current) inputRef.current.value = "";
            }}
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}

      {/* Update existing entities toggle */}
      <div className="flex items-start justify-between gap-4 rounded-lg border border-border px-4 py-3">
        <div>
          <Label htmlFor={`allow-update-${entity.id}`} className="text-sm font-medium text-foreground">
            Actualizar registros existentes
          </Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            Si se activa, las filas que coincidan con {entity.labelSingular}s ya existentes actualizarán sus datos
            en lugar de omitirse o generar un error.
          </p>
        </div>
        <Switch
          id={`allow-update-${entity.id}`}
          checked={allowUpdate}
          onCheckedChange={setAllowUpdate}
        />
      </div>

      <div className="flex justify-end">
        <Button
          onClick={() => importMutation.mutate()}
          disabled={!file || importMutation.isPending}
        >
          {importMutation.isPending ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <UploadCloud className="w-4 h-4 mr-1.5" />
          )}
          Importar {entity.label.toLowerCase()}
        </Button>
      </div>

      <ImportResultModal open={resultOpen} onOpenChange={setResultOpen} result={result} />
    </div>
  );
}
